import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Table, Badge, Form, Modal } from "react-bootstrap";
import { getDataAPI, postDataAPI } from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { exportListExcel, printListPdf } from "../../../utils/listExport";

/**
 * Council > Placement > Jobs.
 * Jobs are already Live when an employer posts them. The council can set them
 * Active / Inactive / Closed, edit the common fields, or delete.
 */
const jobStatus = (s) => {
  const n = Number(s);
  if (n === 1) return <Badge variant="success">Live</Badge>;
  if (n === 2) return <Badge variant="secondary">Closed</Badge>;
  return <Badge variant="warning">Inactive</Badge>;
};

export default function Jobs() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // job being edited

  const load = async (toPage = page) => {
    try {
      const res = await dispatch(
        getDataAPI(`admin/placement/jobs?status=${status}&search=${encodeURIComponent(search)}&page=${toPage}`, token())
      );
      const d = res.data.data || {};
      setRows(d.data || []);
      setMeta({ current_page: d.current_page || 1, last_page: d.last_page || 1, total: d.total || 0 });
      setPage(d.current_page || 1);
    } catch (e) {
      alert({ error: "Could not load jobs." });
    }
  };

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [status]);

  const columns = [
    { key: "title", label: "Title" },
    { key: "sector_trade", label: "Trade" },
    { key: "sub_trade", label: "Sub-Trade" },
    { key: "org_name", label: "Employer" },
    { key: "job_city", label: "City" },
    { key: "posts", label: "Posts" },
    { key: "application_count", label: "Apps" },
    { key: (r) => (Number(r.status) === 1 ? "Live" : Number(r.status) === 2 ? "Closed" : "Inactive"), label: "Status" },
  ];
  const fetchAllRows = async () => {
    const res = await dispatch(getDataAPI(`admin/placement/jobs?status=${status}&search=${encodeURIComponent(search)}&per_page=100000`, token()));
    return (res.data.data && res.data.data.data) || [];
  };
  const exportExcel = async () => { try { exportListExcel(columns, await fetchAllRows(), "placement-jobs"); } catch (e) { alert({ error: "Could not export." }); } };
  const exportPdf = async () => { try { printListPdf("Placement — Jobs", columns, await fetchAllRows()); } catch (e) { alert({ error: "Could not export." }); } };

  const setJobStatus = async (id, s) => {
    setBusy(true);
    try {
      await dispatch(postDataAPI(`admin/placement/jobs/${id}/status`, { status: s }, token()));
      alert({ success: "Status updated." });
      load();
    } catch (e) { alert({ error: "Could not update." }); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job and its applications? This cannot be undone.")) return;
    setBusy(true);
    try {
      await dispatch(postDataAPI(`admin/placement/jobs/${id}/delete`, {}, token()));
      alert({ success: "Job deleted." });
      load();
    } catch (e) { alert({ error: "Could not delete." }); }
    setBusy(false);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await dispatch(postDataAPI(`admin/placement/jobs/${edit.id}/update`, {
        title: edit.title, posts: edit.posts, qualification: edit.qualification,
        experience: edit.experience, salary: edit.salary, specialization: edit.specialization,
        sector_trade: edit.sector_trade, job_state: edit.job_state, job_city: edit.job_city,
        job_address: edit.job_address, description: edit.description,
      }, token()));
      alert({ success: "Job updated." });
      setEdit(null);
      load();
    } catch (e) { alert({ error: "Could not update." }); }
    setBusy(false);
  };

  const field = (k, label) => (
    <Form.Group>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <Form.Control value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
    </Form.Group>
  );

  return (
    <div className="container-fluid py-4">
      <h4 className="mb-3">Placement &mdash; Jobs</h4>

      <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: 12 }}>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Status</Form.Label>
          <Form.Control as="select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 150 }}>
            <option value="all">All</option>
            <option value="1">Live</option>
            <option value="0">Inactive</option>
            <option value="2">Closed</option>
          </Form.Control>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Search</Form.Label>
          <Form.Control value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)} placeholder="Title / sector" style={{ width: 220 }} />
        </Form.Group>
        <Button variant="primary" onClick={() => load(1)} disabled={busy}>Search</Button>
        <div className="ml-auto d-flex" style={{ gap: 8 }}>
          <Button variant="outline-success" size="sm" onClick={exportExcel}>Excel</Button>
          <Button variant="outline-secondary" size="sm" onClick={exportPdf}>PDF</Button>
        </div>
      </div>

      <Table bordered hover responsive size="sm">
        <thead className="thead-light">
          <tr><th>#</th><th>Title</th><th>Employer</th><th>City</th><th>Posts</th><th>Apps</th><th>Status</th><th>Action</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-3">No jobs.</td></tr>}
          {rows.map((j, i) => (
            <tr key={j.id}>
              <td>{i + 1}</td>
              <td>{j.title}<br /><small className="text-muted">{j.sector_trade}</small></td>
              <td>{j.org_name}</td>
              <td>{j.job_city}</td>
              <td>{j.posts}</td>
              <td>{j.application_count}</td>
              <td>{jobStatus(j.status)}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                <Form.Control as="select" size="sm" style={{ display: "inline-block", width: 110 }} value={Number(j.status)}
                  onChange={(e) => setJobStatus(j.id, e.target.value)} disabled={busy}>
                  <option value={1}>Live</option>
                  <option value={0}>Inactive</option>
                  <option value={2}>Closed</option>
                </Form.Control>{" "}
                <Button size="sm" variant="outline-primary" className="mr-1" onClick={() => setEdit(j)}>Edit</Button>
                <Button size="sm" variant="outline-danger" onClick={() => remove(j.id)} disabled={busy}>Delete</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex align-items-center mb-2" style={{ gap: 10 }}>
        <small className="text-muted">Page {meta.current_page} of {meta.last_page} &bull; {meta.total} total</small>
        <div className="ml-auto">
          <Button size="sm" variant="light" disabled={busy || page <= 1} onClick={() => load(page - 1)}>Prev</Button>{" "}
          <Button size="sm" variant="light" disabled={busy || page >= meta.last_page} onClick={() => load(page + 1)}>Next</Button>
        </div>
      </div>

      <Modal show={!!edit} onHide={() => setEdit(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>Edit Job</Modal.Title></Modal.Header>
        <Modal.Body>
          {edit && (
            <div className="row">
              <div className="col-md-6">{field("title", "Title")}</div>
              <div className="col-md-3">{field("posts", "Posts")}</div>
              <div className="col-md-3">{field("salary", "Salary")}</div>
              <div className="col-md-4">{field("qualification", "Qualification")}</div>
              <div className="col-md-4">{field("experience", "Experience")}</div>
              <div className="col-md-4">{field("sector_trade", "Sector / Trade")}</div>
              <div className="col-md-6">{field("specialization", "Specialization")}</div>
              <div className="col-md-3">{field("job_state", "State")}</div>
              <div className="col-md-3">{field("job_city", "City")}</div>
              <div className="col-12">{field("job_address", "Address")}</div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label className="small mb-1">Description</Form.Label>
                  <Form.Control as="textarea" rows={3} value={edit.description || ""}
                    onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
                </Form.Group>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="primary" onClick={saveEdit} disabled={busy}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
